/**
 * CSV Export Utility
 * Provides functions to export data to CSV format
 */

export interface CSVExportOptions {
  filename?: string
  headers?: string[]
  data: Record<string, any>[]
  columns?: string[] // Specific columns to export, if not provided exports all
}

/**
 * Convert data to CSV format
 */
function convertToCSV(data: Record<string, any>[], columns?: string[], headers?: string[]): string {
  if (data.length === 0) {
    return ''
  }

  // Determine columns to export
  const firstRow = data[0]
  const exportColumns = columns || (firstRow ? Object.keys(firstRow) : [])

  // Use provided headers or generate from column keys
  const headerRow =
    headers ||
    exportColumns.map((col) =>
      // Convert camelCase/snake_case to Title Case
      col
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .trim()
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
    )

  // Create CSV rows
  const csvRows: string[] = []

  // Add header row
  csvRows.push(headerRow.map(escapeCSVValue).join(','))

  // Add data rows
  data.forEach((row) => {
    const values = exportColumns.map((col) => {
      const value = row[col]
      return escapeCSVValue(formatValue(value))
    })
    csvRows.push(values.join(','))
  })

  return csvRows.join('\n')
}

/**
 * Escape CSV value (handle quotes, commas, newlines)
 */
function escapeCSVValue(value: string): string {
  if (value === null || value === undefined) {
    return ''
  }

  const stringValue = String(value)

  // If value contains comma, quote, or newline, wrap in quotes and escape existing quotes
  if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
    return `"${stringValue.replace(/"/g, '""')}"`
  }

  return stringValue
}

/**
 * Format value for CSV export
 */
function formatValue(value: any): string {
  if (value === null || value === undefined) {
    return ''
  }

  // Handle boolean
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No'
  }

  // Handle dates
  if (value instanceof Date) {
    return value.toLocaleDateString()
  }

  // Handle arrays
  if (Array.isArray(value)) {
    return value.join('; ')
  }

  // Handle objects
  if (typeof value === 'object') {
    return JSON.stringify(value)
  }

  return String(value)
}

/**
 * Download CSV file
 */
function downloadCSV(csvContent: string, filename: string): void {
  // Create blob
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })

  // Create download link
  const link = document.createElement('a')
  const url = URL.createObjectURL(blob)

  link.setAttribute('href', url)
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'

  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)

  // Clean up
  URL.revokeObjectURL(url)
}

/**
 * Main export function
 * Converts data to CSV and triggers download
 */
export function exportToCSV(options: CSVExportOptions): void {
  const {
    filename = `export_${new Date().toISOString().split('T')[0]}.csv`,
    headers,
    data,
    columns,
  } = options

  if (!data || data.length === 0) {
    console.warn('No data to export')
    return
  }

  const csvContent = convertToCSV(data, columns, headers)
  downloadCSV(csvContent, filename)
}

/**
 * Export current page data
 * Useful for exporting what's currently visible in a table
 */
export function exportCurrentPage<T extends Record<string, any>>(
  data: T[],
  entityName: string,
  columns?: (keyof T)[]
): void {
  const timestamp = new Date().toISOString().split('T')[0]
  const filename = `${entityName.toLowerCase()}_${timestamp}.csv`

  exportToCSV({
    filename,
    data,
    columns: columns as string[],
  })
}

/**
 * Export all data (requires fetching all records)
 * Note: This should be used with caution for large datasets
 */
export async function exportAllData<T extends Record<string, any>>(
  fetchFunction: () => Promise<T[]>,
  entityName: string,
  columns?: (keyof T)[]
): Promise<void> {
  try {
    const allData = await fetchFunction()
    const timestamp = new Date().toISOString().split('T')[0]
    const filename = `${entityName.toLowerCase()}_all_${timestamp}.csv`

    exportToCSV({
      filename,
      data: allData,
      columns: columns as string[],
    })
  } catch (error) {
    console.error('Error exporting data:', error)
    throw new Error('Failed to export data')
  }
}
